"use client";

import { useCallback, useMemo } from "react";
import { useLocale } from "next-intl";

import { localizedTreatmentText, type TreatmentLocaleText } from "@/lib/treatment-locale";
import { normalizeLocale } from "@/lib/i18n";
import type { ClientTreatment, ClientTreatmentStage, QuoteLanguage } from "@/types";

/** Stage name in the given language, falling back en → it → al → key. */
export function stageNameIn(stage: ClientTreatmentStage, locale: QuoteLanguage): string {
  const names = stage.name_i18n as Record<string, string | undefined>;
  return names[locale] || names.en || names.it || names.al || stage.key;
}

/**
 * Treatment title in the given language. `title` is snapshotted in English
 * at spawn, so prefer the catalog item's translated name when it is loaded.
 */
export function treatmentTitleIn(t: ClientTreatment, locale: QuoteLanguage): string {
  const fromCatalog = t.catalog_item?.name
    ? localizedTreatmentText(t.catalog_item.name as TreatmentLocaleText, locale)
    : "";
  return fromCatalog || t.title || t.catalog_item?.name?.en || "Treatment";
}

/**
 * useTreatmentName — THE way to print a catalog item, stage or treatment name
 * on screen. Chart audit B15 (260905): nine call sites read `.name.en` /
 * `name_i18n.en` directly, so Italian clinics saw English names in the armed
 * chip, the log, the cards and every dialog. The UI locale (next-intl) decides;
 * English is the fallback, as in the backend's getTranslatedName().
 */
export function useTreatmentName() {
  const uiLocale = useLocale();
  const locale = useMemo<QuoteLanguage>(() => normalizeLocale(uiLocale), [uiLocale]);

  const item = useCallback(
    (text: TreatmentLocaleText | null | undefined) => localizedTreatmentText(text, locale),
    [locale],
  );
  const stage = useCallback((s: ClientTreatmentStage) => stageNameIn(s, locale), [locale]);
  const treatment = useCallback((t: ClientTreatment) => treatmentTitleIn(t, locale), [locale]);

  return { locale, item, stage, treatment };
}
